import { useEffect, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { Tag } from 'types/lead-management';

interface EditTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tag: Tag) => void;
  editingItem: Tag | null;
  showColorSelector?: boolean;
  labelName?: string;
}

const EditTagModal = ({
  isOpen,
  onClose,
  onSave,
  editingItem: tag,
  showColorSelector = true,
  labelName
}: EditTagModalProps) => {
  const [tagName, setTagName] = useState(tag?.name || '');
  const [selectedColor, setSelectedColor] = useState(tag?.color || '#E6DFED');

  // Update internal state when tag prop changes
  useEffect(() => {
    if (tag) {
      setTagName(tag.name || '');
      setSelectedColor(tag.color || '#E6DFED');
    }
  }, [tag]);

  // Color options based on the Figma design
  const colorOptions = [
    { bgColor: '#FBECCC', textColor: '#3D2D1E', name: 'Yellow' },
    { bgColor: '#DFECDD', textColor: '#24382B', name: 'Green' },
    { bgColor: '#F2E1E9', textColor: '#472636', name: 'Pink' },
    { bgColor: '#D6E5EE', textColor: '#1E3345', name: 'Blue' },
    { bgColor: '#FBD3CB', textColor: '#3D2D1E', name: 'Coral' },
    { bgColor: '#FBEDD1', textColor: '#3D2D1E', name: 'Orange' },
    { bgColor: '#F6DFCC', textColor: '#3D2D1E', name: 'Peach' },
    { bgColor: '#E6DFED', textColor: '#3D2752', name: 'Purple' }
  ];

  const handleSave = () => {
    if (tag && tagName.trim()) {
      const updatedTag: Tag = {
        ...tag,
        name: tagName.trim(),
        color: showColorSelector ? selectedColor : tag.color
      };
      onSave(updatedTag);
      onClose();
    }
  };

  const handleCancel = () => {
    setTagName(tag?.name || '');
    setSelectedColor(tag?.color || '#E6DFED');
    onClose();
  };

  const handleColorSelect = (colorOption: (typeof colorOptions)[0]) => {
    setSelectedColor(colorOption.bgColor);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-edit-modal
    >
      <div className="w-[353px] max-w-[90vw] rounded-lg bg-white shadow-lg">
        <div className="flex flex-col items-center gap-0">
          {/* Content */}
          <div className="flex flex-col items-center gap-1 p-4">
            <div className="flex w-full items-center gap-1 p-1">
              <div className="text-sm font-medium text-darkGrey">
                {labelName}
              </div>
            </div>
            <div className="flex h-11 w-80 items-center gap-2.5 rounded-xl bg-grey10 px-4 py-3">
              <input
                type="text"
                value={tagName}
                onChange={(e) => setTagName(e.target.value.toLowerCase())}
                className="w-full bg-transparent text-sm font-normal text-content1 outline-none placeholder:text-grey40"
                placeholder="Enter tag name..."
              />
            </div>

            {showColorSelector && (
              <>
                <div className="flex w-full items-center gap-1 px-4 py-1">
                  <div className="text-sm font-medium text-darkGrey">
                    Choose colour
                  </div>
                </div>

                {/* Color Options */}
                <div className="flex w-full items-center justify-between gap-2 px-4 py-2">
                  <div className="flex items-center gap-2">
                    {colorOptions.map((colorOption) => (
                      <button
                        key={colorOption.bgColor}
                        type="button"
                        className={clsx(
                          'flex size-7 cursor-pointer items-center justify-center rounded transition-all',
                          selectedColor === colorOption.bgColor &&
                            'border-2 border-content10'
                        )}
                        style={{ backgroundColor: colorOption.bgColor }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleColorSelect(colorOption);
                        }}
                        aria-label={`Select ${colorOption.name} color`}
                      >
                        {selectedColor === colorOption.bgColor && (
                          <Icon
                            name="check"
                            className="size-4 text-content11"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="size-7" />
                </div>
              </>
            )}
          </div>
          <div className="flex w-full flex-col items-end justify-center gap-8 border-t border-grey30 px-4 py-3 pb-4">
            <div className="flex w-60 items-stretch gap-3">
              <Button
                variant="secondaryGreen"
                label="Cancel"
                handleClick={handleCancel}
                className="h-9 flex-1 text-sm font-medium"
              />
              <Button
                variant="primary"
                label="Save"
                handleClick={handleSave}
                className="h-9 flex-1 text-sm font-medium"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTagModal;
